/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Responsive Utilities Hook
 *
 * Provides responsive breakpoint detection for responsive design patterns.
 */

"use client";

import { useEffect, useState } from "react";

/**
 * Breakpoint definitions matching Tailwind CSS
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * Responsive state values
 */
export interface ResponsiveState {
  /**
   * Current window width
   */
  width: number;

  /**
   * Current window height
   */
  height: number;

  /**
   * Is mobile (< 768px)
   */
  isMobile: boolean;

  /**
   * Is tablet (768px - 1023px)
   */
  isTablet: boolean;

  /**
   * Is desktop (>= 1024px)
   */
  isDesktop: boolean;

  /**
   * Current breakpoint key
   */
  breakpoint: Breakpoint;
}

/**
 * Hook for responsive breakpoint detection
 *
 * Uses matchMedia for efficient window resize handling.
 *
 * @example
 * ```typescript
 * const { isMobile, isTablet, isDesktop, breakpoint } = useResponsive();
 *
 * if (isMobile) {
 *   return <MobileView />;
 * }
 *
 * return <DesktopView />;
 * ```
 */
export function useResponsive(): ResponsiveState {
  const [state, setState] = useState<ResponsiveState>(() => {
    // SSR-safe initial state
    if (typeof window === "undefined") {
      return {
        width: 1024,
        height: 768,
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        breakpoint: "lg",
      };
    }

    const width = window.innerWidth;
    const height = window.innerHeight;

    return {
      width,
      height,
      isMobile: width < BREAKPOINTS.md,
      isTablet: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
      isDesktop: width >= BREAKPOINTS.lg,
      breakpoint: getBreakpoint(width),
    };
  });

  useEffect(() => {
    const updateState = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      setState({
        width,
        height,
        isMobile: width < BREAKPOINTS.md,
        isTablet: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
        isDesktop: width >= BREAKPOINTS.lg,
        breakpoint: getBreakpoint(width),
      });
    };

    // Initial update
    updateState();

    // Listen for resize events with debouncing
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateState, 150); // 150ms debounce
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return state;
}

/**
 * Get breakpoint key for given width
 */
function getBreakpoint(width: number): Breakpoint {
  if (width >= BREAKPOINTS["2xl"]) return "2xl";
  if (width >= BREAKPOINTS.xl) return "xl";
  if (width >= BREAKPOINTS.lg) return "lg";
  if (width >= BREAKPOINTS.md) return "md";
  return "sm";
}

/**
 * Hook for media query matching
 *
 * @example
 * ```typescript
 * const isMobile = useMediaQuery("(max-width: 767px)");
 * ```
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    // Fallback for older browsers
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [query]);

  return matches;
}
